import { useCallback } from 'react';
import { useRecruitmentApi } from 'infrastructure/api';
import { ResourceData, useCrudService } from 'framework/crud';
import { cloneDeep } from 'lodash';
import { useListContext } from 'framework/list';
import { useDateService, useDateTimeService } from 'framework/utils';
import { Application, MappedApplication, MappedVacancyBoard, VacancyItem } from './types';
import { usePositionService, PositionType } from 'domain/shared/employee';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { useRecruitingFinanceService } from 'domain/shared/recruitingFinance';
import { MoveToStages } from './constants';
import { Stages } from 'domain/shared/candidate';
import { useVacancyGroupService } from 'domain/shared/newVacancy';

const RECRUITING_POOLS_PATH = '/recruiting-dashboard';
export const REJECT_BOX_ID = 'reject-box';
const FINANCE_FIELDS_MAPPING = {
  compensationFromValue: 'expectedCompensationFromId',
  compensationToValue: 'expectedCompensationToId',
  offerSum: 'offerSumId',
};

export const VACANCY_GROUP_STATUSES = {
  REQUIRES_REVIEW: 'Requires review',
  OPEN: 'Open',
  CLOSED: 'Closed',
  EXCLUDE_ALL_HIRED: 'Exclude completed',
};

const INTERVIEW_KIND_BY_JOB: Record<string, string> = {
  GeneralInterview: 'general',
  RoleRelatedInterview: 'role-related',
};

// Per-event overrides for specific job transitions that must bypass the generic
// pipeline-completion call (`completeJobStep`) and hit a dedicated endpoint instead.
// Add an entry here to route a new transition elsewhere without touching call sites.
type JobTransitionOverride = (applicationId: string, version: number, job: string, payload: any) => Promise<any>;

const useRecruitingBoardService = () => {
  const { sendPostRequest, sendPatchRequest, sendGetRequest } = useRecruitmentApi();
  const { saveSensitiveRecruitingInfo } = useRecruitingFinanceService();
  const { changePriority } = useVacancyGroupService();
  const { addMessage } = useMessageService();
  const { data: { items = [] } = {}, setData = () => {} } = useListContext();
  const { getDateInCurrentTimezone } = useDateTimeService();
  const { getCurrentDateTime } = useDateService();
  const { findAll: findAllPositions } = usePositionService();

  const {
    search: baseSearch,
    update: baseUpdate,
    create: baseCreate,
    ...baseCrud
  } = useCrudService({
    singleResourceUrl: `${RECRUITING_POOLS_PATH}/:id`,
    listResourceUrl: RECRUITING_POOLS_PATH,
    useService: useRecruitmentApi,
  });

  const applicationMap = useCallback(
    (entityId: string, application: Application): MappedApplication => ({
      id: application.id,
      version: application.version,
      entityId,
      candidateId: String(application.candidateId),
      name: application.candidateName,
      vacancyId: application.vacancyId,
      eventTimestamp: getDateInCurrentTimezone(application.applicationPipelineState.stageTransitionTimestamp),
      stage: application.applicationPipelineState.stage,
      tooltipData: application.applicationPipelineState.info,
      undoStates: application.applicationPipelineState.undoStates,
      activeStates: application.applicationPipelineState.jobsStates,
      canDrag: true,
    }),
    [getDateInCurrentTimezone],
  );

  const mapApplicationsToBoard = useCallback(
    (data: VacancyItem[], allPositions: PositionType[]): MappedVacancyBoard[] =>
      data.map((item: VacancyItem) => {
        const level = item.level && item.level !== 'N_A' ? `${item.level} ` : '';
        const technology = item.technology ? ` (${item.technology})` : '';
        const name = `${level}${item.specialization}${technology}`;
        return {
          id: item.id,
          alias: item.alias,
          level: item.level,
          specialization: item.specialization,
          name,
          isCustomPostion: !allPositions.find(position => position.name === item.specialization),
          hired: item.hired,
          isLegacy: item.isLegacy,
          priority: item.priority,
          needHire: item.needHire,
          forecastDate: item.forecastDate,
          isVacancyInsideGroupExpired: item.isVacancyInsideGroupExpired,
          items: item.applications.map(application => applicationMap(item.id, application)),
          specificNote: item.specificNote ? item.specificNote : 'Without specific',
          status: item.status,
          viewStatus: item.viewStatus,
          areVacancyChangesRequested: item.areVacancyChangesRequested,
        };
      }),
    [applicationMap],
  );

  const updateLocalState = useCallback(
    (updatedApplication: MappedApplication, moveVacancyGroupAction?: boolean) => {
      setData((prev: any) => ({
        ...prev,
        items: prev.items.map((entity: MappedVacancyBoard) => {
          if (entity.id === updatedApplication.entityId) {
            return {
              ...entity,
              items: moveVacancyGroupAction
                ? [...entity.items, updatedApplication]
                : entity.items.map(item => (item.id === updatedApplication.id ? updatedApplication : item)),
            };
          }

          return entity;
        }),
      }));
    },
    [setData],
  );

  const removeApplicationFromState = useCallback(
    (removedApplication: MappedVacancyBoard, application: MappedApplication) => {
      setData((prev: any) => ({
        ...prev,
        items: prev.items.map((entity: MappedVacancyBoard) => {
          if (entity.id === removedApplication.id) {
            const findRemovedApplication = entity.items.find(item => item.candidateId === application.candidateId);

            return {
              ...entity,
              items: entity.items.filter(item => item.id !== findRemovedApplication?.id),
            };
          }

          return entity;
        }),
      }));
    },
    [setData],
  );

  const moveStageAction = useCallback(
    (vacancyId: string, version: number, applicationId: number, stage: string) => {
      const targetEntity = items.find(entity => entity.id === vacancyId);
      if (!targetEntity) return null;

      const prevState = targetEntity.items.find((item: MappedApplication) => item.id === applicationId);

      return {
        newState: {
          ...prevState,
          eventTimestamp: getCurrentDateTime().toISOString(),
          stage,
          canDrag: false,
        },
        prevState,
      };
    },
    [items, getCurrentDateTime],
  );

  const moveStage = useCallback(
    async (vacancyId: string, version: number, applicationId: number, stage: string, payload: any = null) => {
      const updatedEntity = moveStageAction(vacancyId, version, applicationId, stage);
      if (!updatedEntity) return null;

      const { newState, prevState } = updatedEntity;

      updateLocalState(newState);
      if (payload?.resumeId?.length) {
        // workaround to correctly pass single artifact
        // eslint-disable-next-line no-param-reassign, prefer-destructuring
        payload.resumeId = payload.resumeId[0];
      }
      if (payload?.screeningMaterialsId) {
        // same as above (workaround to correctly pass single artifact)
        // eslint-disable-next-line no-param-reassign, prefer-destructuring
        payload.screeningMaterialsId = payload.screeningMaterialsId[0];
      }
      try {
        const res = await sendPostRequest(`/candidates/applications/${newState.id}/pipeline/move`, {
          targetStage: MoveToStages[newState.stage as Exclude<Stages, Stages.Rejected>],
          version,
          payload,
        });
        const resJson = await res.json();
        updateLocalState(applicationMap(newState.entityId, resJson));
        return resJson;
      } catch (error: any) {
        try {
          const errorToParse = error instanceof Response ? error.clone() : error;
          const errorJson = await errorToParse.json();
          const errorMessage = errorJson?.message;

          if (errorMessage) {
            addMessage(new ErrorMessage(errorMessage));
          }
        } catch {
          // no-op: let form-level handlers process unknown error shapes
        }
        updateLocalState(prevState);
        throw error;
      }
    },
    [sendPostRequest, applicationMap, moveStageAction, updateLocalState, addMessage],
  );

  const moveVacancyGroupAction = useCallback(
    (application: MappedApplication, applicationId: string) => {
      const targetEntity = items.find(entity => entity.id === applicationId);
      if (!targetEntity) return null;

      const newApplicationState = {
        ...application,
        entityId: targetEntity.id,
        eventTimestamp: getCurrentDateTime().toISOString(),
        canDrag: false,
      };

      return {
        newState: newApplicationState,
        prevState: application,
      };
    },
    [items, getCurrentDateTime],
  );

  const moveVacancyGroup = useCallback(
    async (application: MappedApplication, vacancyGroupId: string, vacancyGroupAlias: string, payload: any = null) => {
      const updatedEntity = moveVacancyGroupAction(application, vacancyGroupId);
      const previousEntity = items.find(entity => entity.items.find((item: any) => item.id === application.id));

      if (!updatedEntity || !previousEntity) return null;

      const { newState, prevState } = updatedEntity;

      updateLocalState(newState, true);
      removeApplicationFromState(previousEntity, application);

      try {
        const res = await sendPatchRequest(`/candidates/applications/${application.id}/vacancy-group/change`, {
          version: application.version,
          vacancyGroupAlias,
          payload,
        });
        const resJson = await res.json();
        updateLocalState(applicationMap(newState.entityId, resJson));
        return resJson;
      } catch (error) {
        updateLocalState(prevState);
        updateLocalState(previousEntity);
        return error;
      }
    },
    [moveVacancyGroupAction, items, updateLocalState, removeApplicationFromState, sendPatchRequest, applicationMap],
  );

  const completeJobStep = useCallback(
    async (applicationId: string, version: number, job: string, event: string, payload: any) => {
      try {
        if (payload?.resumeId?.length) {
          // workaround to correctly pass single artifact
          // eslint-disable-next-line no-param-reassign, prefer-destructuring
          payload.resumeId = payload.resumeId[0];
        }
        if (payload?.screeningMaterialsId) {
          // same as above (workaround to correctly pass single artifact)
          // eslint-disable-next-line no-param-reassign, prefer-destructuring
          payload.screeningMaterialsId = payload.screeningMaterialsId[0];
        }
        const resultPayload = await handleFinanceInfo(payload);
        const res = await sendPostRequest(`/candidates/applications/${applicationId}/pipeline/jobs/complete`, {
          event,
          version,
          jobName: job,
          payload: resultPayload,
        });
        const resJson = await res.json();
        return resJson;
      } catch (error: any) {
        const errorJson = await error.json();
        if (errorJson.message) {
          addMessage(new ErrorMessage(errorJson.message));
        } else {
          addMessage(new ErrorMessage('Error during job completion'));
        }
        return error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendPostRequest, addMessage],
  );

  const resetInterview = useCallback(
    async (applicationId: string, version: number, job: string) => {
      try {
        const interviewKind = INTERVIEW_KIND_BY_JOB[job];
        const res = await sendPostRequest(
          `/interviews/applications/${applicationId}/${interviewKind}/reset`,
          { version },
        );
        const resJson = await res.json();
        return resJson;
      } catch (error: any) {
        const errorJson = await error.json();
        if (errorJson.message) {
          addMessage(new ErrorMessage(errorJson.message));
        } else {
          addMessage(new ErrorMessage('Error during job completion'));
        }
        return error;
      }
    },
    [sendPostRequest, addMessage],
  );

  const jobTransitionOverrides: Record<string, JobTransitionOverride> = {
    GeneralInterviewReset: (applicationId, version, job) => resetInterview(applicationId, version, job),
    RoleRelatedInterviewReset: (applicationId, version, job) => resetInterview(applicationId, version, job),
  };

  const performJobTransition = useCallback(
    async (applicationId: string, version: number, job: string, event: string, payload: any) => {
      const override = jobTransitionOverrides[event];
      if (override) {
        return override(applicationId, version, job, payload);
      }
      return completeJobStep(applicationId, version, job, event, payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [completeJobStep, resetInterview],
  );

  const handleFinanceInfo = async (payload: any) => {
    if (!payload) return null;

    const hasFinanceFields = Object.keys(FINANCE_FIELDS_MAPPING).some(field => field in payload);

    if (!hasFinanceFields) return payload;

    return Object.entries(FINANCE_FIELDS_MAPPING).reduce(async (accPromise, [fieldName, idField]) => {
      const acc = await accPromise;

      if (!(fieldName in acc)) return acc;

      const response = await saveSensitiveRecruitingInfo(acc[fieldName]);
      const { id } = response;

      const { [fieldName]: _, ...rest } = acc;
      return {
        ...rest,
        [idField]: id,
      };
    }, Promise.resolve(payload));
  };

  const updatePriority = useCallback(
    async (vacancyId: string, priority: string) => {
      const updatedVacancy = await changePriority(vacancyId, priority);
      setData((prev: any) => ({
        ...prev,
        items: prev.items.map((entity: MappedVacancyBoard) =>
          entity.id === vacancyId
            ? {
                ...entity,
                priority,
              }
            : entity,
        ),
      }));

      return updatedVacancy;
    },
    [setData, changePriority],
  );

  const search = useCallback(
    async (request: ResourceData) => {
      try {
        const params = cloneDeep(request);
        const searchString = params.filter?.['specification:search']?.eq || '';

        if (searchString && params.filter) {
          delete params.filter?.['specification:search'];
          params.filter['specification:vacancyGroupBySpecialization'] = {
            ct: searchString,
          };
        }

        const allPositions = await findAllPositions();

        const response = await baseSearch({
          ...params,
          pageSize: 200,
        });

        return {
          ...response,
          result: mapApplicationsToBoard(response.result as VacancyItem[], allPositions as PositionType[]),
        };
      } catch (error) {
        return error;
      }
    },
    [baseSearch, findAllPositions, mapApplicationsToBoard],
  );

  const searchCandidateApplicationsById = useCallback(
    async (request: ResourceData) => {
      try {
        const allPositions = await findAllPositions();

        const response = await baseSearch({
          request,
          pageSize: 200,
        });

        return {
          ...response,
          result: mapApplicationsToBoard(response.result as VacancyItem[], allPositions as PositionType[]),
        };
      } catch (error) {
        return error;
      }
    },
    [baseSearch, findAllPositions, mapApplicationsToBoard],
  );

  const getCandidateDetailsById = async (candidateId: StringOrNumber) => {
    const response = await sendGetRequest(`${RECRUITING_POOLS_PATH}/candidates/${candidateId}?notesSize=2`);
    return response.json();
  };

  return {
    search,
    completeJobStep,
    performJobTransition,
    moveStage,
    updatePriority,
    moveVacancyGroup,
    getCandidateDetailsById,
    ...baseCrud,
    searchCandidateApplicationsById,
  };
};

export default useRecruitingBoardService;
